<template>
  <div id="stats" class="row g-2 justify-content-center my-5">
    <div class="col-xxl-6 col-xl-7 col-lg-8 col-md-11 hyphenate">
      <h1>Verspätungen in Deutschland</h1>
      <p>
        Zeige die durchschnittliche Verspätung pro Bahnhof im gewählten Zeitraum auf einer Karte an.
      </p>
      <div v-if="sliderDates.length" class="stats-picker">
        <Slider
          class="slider"
          v-if="values.length"
          v-model="values"
          :merge="merge"
          :max="sliderDates.length - 1"
          :format="format"
          style="width: 200px"
        />
        <input
          class="btn btn-primary"
          type="button"
          value="Karte anzeigen"
          v-on:click="updatePlot"
        />
      </div>
      <figure class="figure my-5" v-if="plotSrc" ref="stats-image">
        <img
          :src="plotSrc"
          class="figure-img img-fluid rounded"
          alt="Eine Karte mit durchschnittlichen Verspätungen pro Bahnhof"
        />
        <figcaption class="figure-caption">
          Durchschnittliche Verspätungen pro Bahnhof im Zeitraum von
          {{ sliderDates[values[0]] }} bis {{ sliderDates[values[1]] }}. Je größer die Kreise, desto
          mehr Zughalte gab es an diesem Bahnhof.
        </figcaption>
      </figure>
      <em v-else class="d-block mt-4 text-center"
        >Zum Anzeigen der Karte den Zeitraum auswählen und Karte anzeigen klicken</em
      >
    </div>
  </div>
</template>

<script setup lang="ts">
definePageMeta({ layout: 'default' })

useHead({
  title: 'Bahnhofs-Statistiken',
  meta: [
    {
      name: 'description',
      content: 'Durchschnittliche Verspätung pro Bahnhof im Zeitverlauf.'
    }
  ]
})

import { ref, computed, onMounted, useTemplateRef } from 'vue'
import { useMainStore } from '~/stores/main'
import { storeToRefs } from 'pinia'
import Slider from '@vueform/slider'
import { default as dayjs } from 'dayjs'

const store = useMainStore()
const config = useRuntimeConfig()
const { progressing } = storeToRefs(store)

const statsImage = useTemplateRef('stats-image')
const values = ref<number[]>([])
const sliderDates = ref<string[]>([])
const plotSrc = ref('')

const merge = computed(() => {
  /*
   * When the slider ends are this many ticks apart, merge their labels
   */
  const default_slider_length = 400
  const distance_to_merge = 120
  const n_ticks = sliderDates.value.length || 1
  return Math.ceil((distance_to_merge * n_ticks) / default_slider_length)
})

function initialize() {
  progressing.value = true
  fetch(config.public.requestBaseUrl + 'api/stationplot/limits', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })
    .then((response) => store.displayFetchError(response))
    .then((response) => response.json())
    .then((limits) => {
      progressing.value = false
      limits.min = dayjs(limits.min, 'YYYY-MM-DD')
      limits.max = dayjs(limits.max, 'YYYY-MM-DD')

      const dates = [limits.min] as dayjs.Dayjs[]
      while (dates[dates.length - 1].isBefore(limits.max)) {
        dates.push(dates[dates.length - 1].add(limits.freq, 'hours'))
      }
      sliderDates.value = dates.map((date) => date.format('DD.MM.YYYY'))
      values.value = [0, sliderDates.value.length - 1]
    })
}

function updatePlot() {
  progressing.value = true
  fetch(
    config.public.requestBaseUrl +
      'api/stationplot/' +
      sliderDates.value[values.value[0]] +
      '-' +
      sliderDates.value[values.value[1]] +
      '.webp'
  )
    .then((response) => store.displayFetchError(response))
    .then((response) => response.blob())
    .then((imgBlob) => {
      progressing.value = false
      plotSrc.value = URL.createObjectURL(imgBlob)
      statsImage.value?.scrollIntoView({ block: 'start', behavior: 'smooth' })
    })
}
function format(index: number) {
  return sliderDates.value[index]
}
onMounted(() => {
  initialize()
})
</script>

<style lang="scss">
@import '@vueform/slider/themes/default.css';

.stats-picker {
  display: flex;
  justify-content: space-around;
  margin: auto;
  width: 100%;
  gap: 60px;
  align-items: center;
  height: max-content;
  margin-top: 3em;

  .slider {
    width: max-content;
    flex-grow: 1;
  }
}

.stats-image {
  max-height: 70vh;
  max-width: 100%;
  margin: auto;
  display: block;
}

@media (max-width: 1000px) {
  .stats-picker {
    flex-wrap: wrap;
  }
}

.slider {
  --slider-connect-bg: #{$primary};
  --slider-tooltip-bg: #{$primary};
  --slider-handle-ring-color: #3b82f630;
}
</style>
